// gioco/rilevamento.ts — analisi dei volti sul telefono (MediaPipe Face Landmarker): il proprio e quello
// dell'avversario. Nessuna immagine esce dal telefono: al server arriva solo "ha riso alle hh:mm:ss.mmm".
//
// MediaPipe si carica solo quando serve (è grande) dai file serviti dall'app stessa: il motore WebAssembly in
// /mediapipe e il modello in /modelli (copiati e scaricati quando si costruisce l'immagine, vedi nginx.Dockerfile).
// Se non si carica (telefono troppo vecchio, file mancanti) si gioca lo stesso con il pulsante «Ha riso!».
import { RilevatoreRisata, type Blendshape, type EventoRilevamento, type ParametriRilevamento } from "../../../condiviso/risata.ts";
import { eBooleano, eNumero, leggiImpostazione } from "../servizi/impostazioni.ts";
import { CARTELLA_MEDIAPIPE, IMP_APP, MODELLO_VOLTO } from "../costanti.ts";

/** Il minimo di MediaPipe che serve (così il resto dell'app non dipende dalla libreria). */
interface Categoria {
  categoryName: string;
  score: number;
}
export interface PuntoNormalizzato {
  x: number;
  y: number;
  z: number;
}
export interface RisultatoMediaPipe {
  faceBlendshapes?: { categories: Categoria[] }[];
  faceLandmarks?: PuntoNormalizzato[][];
  facialTransformationMatrixes?: { data: ArrayLike<number> }[];
}
export interface FaceLandmarker {
  detectForVideo(video: HTMLVideoElement, t: number): RisultatoMediaPipe;
  close(): void;
}

/**
 * Il proprio volto analizzato da qualcun altro (il motore dei filtri, che lo analizza già a ogni fotogramma):
 * così lo stesso analizzatore non lavora due volte. `versione` cresce a ogni fotogramma analizzato.
 */
export interface SorgenteVolto {
  /** `t` = ora del server nel momento in cui il fotogramma è stato analizzato (non quando lo si legge). */
  ultimo(): { versione: number; espressioni: Blendshape | null; t: number };
}

/**
 * Due analizzatori, uno per volto: in modalità VIDEO MediaPipe segue il volto da un fotogramma all'altro,
 * quindi lo stesso analizzatore non può alternare due persone.
 */
export interface Analizzatori {
  io: FaceLandmarker;
  avversario: FaceLandmarker;
}

let caricamento: Promise<Analizzatori | null> | null = null;
/** v034: l'analisi del volto gira sulla CPU (la GPU non c'è o non va): telefono probabilmente lento. */
export let analisiSullaCpu = false;

async function caricaMediaPipe(): Promise<Analizzatori | null> {
  try {
    const v = await import("@mediapipe/tasks-vision");
    const fileset = await v.FilesetResolver.forVisionTasks(CARTELLA_MEDIAPIPE);
    const crea = (delegate: "GPU" | "CPU") =>
      v.FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODELLO_VOLTO, delegate },
        runningMode: "VIDEO",
        numFaces: 1,
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true, // per i filtri in 3D
      }) as unknown as Promise<FaceLandmarker>;
    // La GPU è molto più veloce, ma non c'è dappertutto: in quel caso la CPU (v034: si ricorda, la qualità automatica
    // dei filtri parte più in basso).
    const uno = () =>
      crea("GPU").catch(() => {
        analisiSullaCpu = true;
        return crea("CPU");
      });
    return { io: await uno(), avversario: await uno() };
  } catch {
    caricamento = null; // si potrà riprovare alla prossima partita
    return null;
  }
}

/** Carica MediaPipe una volta sola, a prescindere da chi lo usa (rilevamento della risata o filtri). */
export function caricaAnalizzatori(): Promise<Analizzatori | null> {
  caricamento ??= caricaMediaPipe();
  return caricamento;
}

/** Analizzatori per il rilevamento della risata: null se il rilevamento è spento dal pannello o MediaPipe manca. */
export function preparaRilevamento(): Promise<Analizzatori | null> {
  if (!leggiImpostazione(IMP_APP.rilevamentoAttivo, true, eBooleano)) return Promise.resolve(null);
  return caricaAnalizzatori();
}

export function parametriRilevamento(): ParametriRilevamento {
  const n = (k: string, ripiego: number) => leggiImpostazione(k, ripiego, eNumero);
  return {
    soglia: n(IMP_APP.rilevamentoSoglia, 0.45),
    fotogrammi: n(IMP_APP.rilevamentoFotogrammi, 4),
    smorzamento: n(IMP_APP.rilevamentoSmorzamento, 0.5),
    pesoSorriso: n(IMP_APP.rilevamentoPesoSorriso, 0.5),
    pesoBocca: n(IMP_APP.rilevamentoPesoBocca, 0.35),
    pesoGuance: n(IMP_APP.rilevamentoPesoGuance, 0.15),
    sorrisoMinimo: n(IMP_APP.rilevamentoSorrisoMinimo, 0.3),
    boccaMinima: n(IMP_APP.rilevamentoBoccaMinima, 0.25),
    durataMinimaMs: n(IMP_APP.rilevamentoDurataMinimaMs, 700),
    refrattarioMs: n(IMP_APP.rilevamentoRefrattarioMs, 3000),
    voltoAssenteMs: n(IMP_APP.rilevamentoVoltoAssenteMs, 1000),
  };
}

export function blendshapeDa(r: RisultatoMediaPipe): Blendshape | null {
  const cat = r.faceBlendshapes?.[0]?.categories;
  if (!cat?.length) return null;
  const b: Record<string, number> = {};
  for (const c of cat) b[c.categoryName] = c.score;
  return b;
}

export interface Analisi {
  /** Punteggi attuali 0–1 (per il "risatometro" a schermo). */
  punteggi(): { io: number; avversario: number };
  azzera(): void;
  ferma(): void;
}

/**
 * Analizza alternando i due video (il proprio e quello dell'avversario) al ritmo app.rilevamento_fotogrammi_secondo.
 * `orario` dà l'ora del server: gli eventi partono già datati.
 * Il proprio volto può arrivare già analizzato dal motore dei filtri (SorgenteVolto): stesso ritmo, nessun doppio lavoro.
 */
export function avviaAnalisi(
  fl: Analizzatori,
  video: { io: HTMLVideoElement | SorgenteVolto; avversario: HTMLVideoElement },
  orario: () => number,
  suEvento: (chi: "io" | "avversario", e: EventoRilevamento) => void,
): Analisi {
  const p = parametriRilevamento();
  const rilevatori = { io: new RilevatoreRisata(p), avversario: new RilevatoreRisata(p) };
  const fps = leggiImpostazione(IMP_APP.rilevamentoFps, 15, eNumero);
  const intervalloMs = 1000 / Math.max(1, fps);
  let turno: "io" | "avversario" = "io";
  let ultimo = 0;
  const ultimoTempoVideo = { io: -1, avversario: -1 };
  let fermo = false;
  let richiesta = 0;
  let ultimaVersione = -1;

  const ciclo = (adesso: number) => {
    if (fermo) return;
    richiesta = requestAnimationFrame(ciclo);
    if (adesso - ultimo < intervalloMs / 2) return; // due video: metà intervallo ciascuno
    ultimo = adesso;
    const chi = turno;
    turno = turno === "io" ? "avversario" : "io";
    const v = video[chi];
    if (!(v instanceof HTMLVideoElement)) {
      // Già analizzato dal motore dei filtri: si prende l'ultimo risultato (se è nuovo).
      const u = v.ultimo();
      if (u.versione === ultimaVersione) return;
      ultimaVersione = u.versione;
      for (const e of rilevatori[chi].fotogramma(u.t, u.espressioni)) suEvento(chi, e);
      return;
    }
    if (v.readyState < 2 || v.videoWidth === 0) return;
    // MediaPipe in modalità VIDEO vuole tempi crescenti (per ciascun analizzatore).
    const t = Math.max(adesso, ultimoTempoVideo[chi] + 1);
    ultimoTempoVideo[chi] = t;
    let b: Blendshape | null = null;
    try {
      b = blendshapeDa(fl[chi].detectForVideo(v, t));
    } catch {
      return;
    }
    for (const e of rilevatori[chi].fotogramma(orario(), b)) suEvento(chi, e);
  };
  richiesta = requestAnimationFrame(ciclo);

  return {
    punteggi: () => ({ io: rilevatori.io.punteggioAttuale, avversario: rilevatori.avversario.punteggioAttuale }),
    azzera: () => {
      const t = orario();
      rilevatori.io.azzera(t);
      rilevatori.avversario.azzera(t);
    },
    ferma: () => {
      fermo = true;
      cancelAnimationFrame(richiesta);
    },
  };
}
